import React from 'react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import { formatDate, getFlagPath, getImageUrl } from '../../Functions/Utilities'
import { resolveCreator, resolvePostTypeName, getNationalityFontClass } from '../../Functions/imigoWebConfig'
import { useLanguage } from '../../Context/LanguageContext'

const isRTL = (nationality) => {
  if (!nationality || !nationality.data || !nationality.data.attributes) return false
  if (typeof nationality.data.attributes.is_rtl === 'boolean') {
    return nationality.data.attributes.is_rtl
  }
  const rtlLangs = ['ar', 'fa', 'he', 'ur', 'ps', 'dv', 'ku', 'yi']
  const lang = nationality.data.attributes.language_query?.toLowerCase()
  return rtlLangs.includes(lang)
}

const PostCard = ({ post, postType, routeSegment }) => {
  const { getLocalizedPath } = useLanguage()
  const { id, attributes } = post
  const {
    title,
    description,
    createdAt,
    picture,
    post_type,
    start,
    is_imigo_partner,
    nationality,
    creator,
  } = attributes

  const isRtl = isRTL(nationality)
  const flagPath = getFlagPath(nationality)
  const fontClass = getNationalityFontClass(nationality)
  const type = resolvePostTypeName(post_type || postType, routeSegment)
  const author = resolveCreator(creator)

  const stripHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return doc.body.textContent || ''
  }

  const handleImageError = (e) => {
    e.target.src = 'https://panel.imigo.app/uploads/medium_logo_baf7005f31.jpg'
  }

  const getPostUrl = () => {
    const slug = attributes.slug || id
    let postTypeUrl = type.toLowerCase()

    if (postTypeUrl === 'article') postTypeUrl = 'articles'
    else if (postTypeUrl === 'business') postTypeUrl = 'businesses'
    else if (postTypeUrl === 'event') postTypeUrl = 'events'
    else if (postTypeUrl === 'activity') postTypeUrl = 'activities'
    else if (postTypeUrl === 'marketplace') postTypeUrl = 'marketplace'

    return getLocalizedPath(`/${postTypeUrl}/${slug}`)
  }

  const getShortDescription = () => {
    const strippedText = stripHtml(description)
    return strippedText.length > 100 ? `${strippedText.substring(0, 100)}...` : strippedText
  }

  return (
    <article className={`post-card bg-white rounded-2xl overflow-hidden ${fontClass} ${isRtl ? 'rtl' : 'ltr'}`} dir={isRtl ? 'rtl' : 'ltr'}>
      <Link to={getPostUrl()} className="block">
        <div className="post-image">
          <img
            src={getImageUrl(picture, 'medium')}
            alt={title}
            onError={handleImageError}
            loading="lazy"
          />
          {is_imigo_partner && (
            <div className="absolute top-2 right-2 bg-[#e8a13a] text-white text-[10px] px-2 py-0.5 rounded-full font-bold uppercase">
              Partner
            </div>
          )}
          {flagPath && (
            <div className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm rounded-full p-0.5 shadow-sm">
              <img
                src={flagPath}
                alt=""
                className="w-5 h-5 object-cover rounded-full"
                onError={(e) => { e.target.style.display = 'none' }}
              />
            </div>
          )}
          <div className={`absolute bottom-2 ${isRtl ? 'right-2' : 'left-2'}`}>
            <span className="type-badge">{type}</span>
          </div>
        </div>
      </Link>

      <div className="post-content">
        <div className={`flex items-center gap-2 mb-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
          {author.picture ? (
            <img src={author.picture} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-[#0f8a8d] text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">
              {author.name.charAt(0)}
            </div>
          )}
          <span className="text-xs text-[#5b6b78] font-medium truncate">{author.name}</span>
        </div>

        <Link to={getPostUrl()} className="block">
          <h3 className={`mb-1.5 hover:text-[#0f8a8d] transition-colors ${isRtl ? 'text-right' : 'text-left'}`}>
            {title}
          </h3>
        </Link>

        <p className={`text-xs text-[#5b6b78] mb-2 line-clamp-2 leading-relaxed ${isRtl ? 'text-right' : 'text-left'}`}>
          {getShortDescription()}
        </p>

        <div className={`flex justify-between items-center text-[11px] text-[#8a9aa6] ${isRtl ? 'flex-row-reverse' : ''}`}>
          <span>{formatDate(createdAt)}</span>
          {start && <span>{new Date(start).toLocaleDateString()}</span>}
        </div>
      </div>
    </article>
  )
}

PostCard.propTypes = {
  post: PropTypes.object.isRequired,
  postType: PropTypes.string,
  routeSegment: PropTypes.string,
}

export default PostCard
